import type { ReactElement, ReactNode } from 'react'

import type { Api, KeycloakState, Page, Router } from '@subaio/utils'
import type { Container as ContainerBase, Params } from '@subaio/webapp/src/containers'

import type { UserAuthStatus } from 'lib/store'

import { LoadingScreen } from 'components/LoadingScreen'

export type Extensions<
	TParams extends Params,
	TState extends Record<string, unknown>,
	TDependencies extends keyof TState,
> = {
	path?: string
	authentication: /** Can only be accessed by an authenticated user */
		| { type: 'user' }
		/** Can only be accessed by a developer */
		| { type: 'developer'; roles: Array<string> }
		/** Can always be accessed */
		| { type: 'public' }
	title?: ReactNode
	navTitle?: ReactNode
	description?: ReactNode
	Sidebar?: (props: Props<TState, TDependencies> & TParams & TState) => ReactElement | null
}

export type Props<TState extends Record<string, unknown>, TDependencies extends keyof TState> = {
	userAuthStatus: UserAuthStatus
	developerAuthStatus: KeycloakState['status']
	query: Page['query']
	statuspageApi: Api
	invalidateSelf: (...dependencies: Array<TDependencies | string>) => Promise<void>
	setData: (update: Partial<TState>) => void
	setToken: (options?: { token: string; userGroupId: string }) => void
	loginDeveloper: () => void
}

export type InitProps = {
	query: Page['query']
	statuspageApi: Api
	router: Router
	setToken: (options?: { token: string; userGroupId: string }) => void
	updatePage: (page: Page) => void
}

export type Container<
	TState extends Record<string, unknown> = Record<string, any>,
	TParams extends Params = Record<string, any>,
	TDefaults extends keyof TState = keyof TState,
	TDependencies extends keyof TState = keyof TState,
> = ContainerBase<
	Extensions<TParams, TState, TDependencies>,
	Props<TState, TDependencies>,
	InitProps,
	TState,
	TParams,
	TDefaults,
	TDependencies
>

export const asContainer =
	<TState extends Record<string, unknown> = {}, TParams extends Params = {}>() =>
	<TDefaults extends keyof TState = never, TDependencies extends keyof TState = never>(
		container: Omit<
			ContainerBase<
				Extensions<TParams, TState, TDependencies>,
				Props<TState, TDependencies>,
				InitProps,
				TState,
				TParams,
				TDefaults,
				TDependencies
			>,
			'Loading'
		>,
	) =>
		({
			...container,
			Loading: () => <LoadingScreen />,
		}) as ContainerBase<
			Extensions<TParams, TState, TDependencies>,
			Props<TState, TDependencies>,
			InitProps,
			TState,
			TParams,
			TDefaults,
			TDependencies
		>
